import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../utils/AxiosInstance";
import { useAuth } from "../context/AuthContext";
import SearchContainer from "../components/SearchContainer";

const PAGE_SIZE = 10; // You can change or make configurable

const ScoutMonitor = () => {
  const { accessToken } = useAuth();

  // Keywords and monitor results
  const [keywords, setKeywords] = useState([]);
  const [monitorResults, setMonitorResults] = useState([]);
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [monitorError, setMonitorError] = useState("");

  // Pagination for monitor results
  const [monitorPage, setMonitorPage] = useState(1);
  const [monitorTotalPages, setMonitorTotalPages] = useState(1);

  // Ransomware victims data and pagination
  const [ransomVictims, setRansomVictims] = useState([]);
  const [ransomLoading, setRansomLoading] = useState(false);
  const [ransomError, setRansomError] = useState("");
  const [ransomPage, setRansomPage] = useState(1);
  const [ransomTotalPages, setRansomTotalPages] = useState(1);

  // Fetch Scout Monitor keywords and monitor results
  const fetchMonitorData = useCallback(async () => {
    setMonitorLoading(true);
    try {
      const response = await axiosInstance.get("/discover/scout/monitor/", {
        headers: { Authorization: `Bearer ${accessToken}` },
        params: { page: monitorPage, page_size: PAGE_SIZE },
      });

      setKeywords(response.data.keywords || []);
      setMonitorResults(response.data.results || []);
      setMonitorTotalPages(
        response.data.total_count
          ? Math.ceil(response.data.total_count / PAGE_SIZE)
          : 1
      );
      setMonitorError("");
    } catch (err) {
      console.error(err);
      setMonitorError("Error loading monitor data.");
    } finally {
      setMonitorLoading(false);
    }
  }, [accessToken, monitorPage]);

  // Fetch ransomware victims filtered by keywords and paginated
  const fetchRansomVictims = useCallback(async () => {
    if (keywords.length === 0) {
      setRansomVictims([]);
      setRansomTotalPages(1);
      setRansomError("");
      return;
    }
    setRansomLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", ransomPage);
      params.append("page_size", PAGE_SIZE);
      keywords.forEach(({ keyword }) => {
        if (keyword) params.append("keywords", keyword);
      });

      const response = await axiosInstance.get(
        `/insights/ransomware-victims/?${params.toString()}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      setRansomVictims(response.data.results || []);
      setRansomTotalPages(
        response.data.count ? Math.ceil(response.data.count / PAGE_SIZE) : 1
      );
      setRansomError("");
    } catch (err) {
      console.error(err);
      setRansomError("Error loading ransomware victims.");
      setRansomVictims([]);
      setRansomTotalPages(1);
    } finally {
      setRansomLoading(false);
    }
  }, [accessToken, keywords, ransomPage]);

  // Unified refresh button to reload both monitor and ransomware victims data
  const handleRefresh = async () => {
    setMonitorPage(1);
    setRansomPage(1);
    await fetchMonitorData();
    await fetchRansomVictims();
  };

  // Load data on page or keywords change
  useEffect(() => {
    fetchMonitorData();
  }, [fetchMonitorData]);

  useEffect(() => {
    fetchRansomVictims();
  }, [fetchRansomVictims]);

  // Keyword delete handler
  const deleteKeyword = async (id) => {
    try {
      await axiosInstance.delete(`/discover/scout/keywords/${id}/delete/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setMonitorPage(1);
      await fetchMonitorData();
      // After keyword change, reset ransom page and refresh ransomware victims
      setRansomPage(1);
      await fetchRansomVictims();
    } catch (err) {
      console.error(err);
      setMonitorError("Error deleting keyword.");
    }
  };

  // Pagination handlers
  const onMonitorPrevPage = () => {
    if (monitorPage > 1) setMonitorPage((p) => p - 1);
  };
  const onMonitorNextPage = () => {
    if (monitorPage < monitorTotalPages) setMonitorPage((p) => p + 1);
  };
  const onRansomPrevPage = () => {
    if (ransomPage > 1) setRansomPage((p) => p - 1);
  };
  const onRansomNextPage = () => {
    if (ransomPage < ransomTotalPages) setRansomPage((p) => p + 1);
  };

  // Download JSON helper
  const downloadJson = (jsonData, filename) => {
    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <SearchContainer>
      <div className="bg-gray-900 text-white py-6 px-4 min-h-screen max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-4 text-center">Scout Monitor</h2>
        <p className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-6">
          This page displays real-time monitoring results for your keyword
          watchlist across sources like Certstream, OpenPhish, PhishTank, and
          LocalDB. You can also view related ransomware victims from
          Ransomware.live based on your monitored keywords.
        </p>

        {/* Watched Keywords and Refresh */}
        <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
          <h3 className="text-xl font-semibold whitespace-nowrap">
            Watched Keywords
          </h3>
          <button
            onClick={handleRefresh}
            disabled={monitorLoading || ransomLoading}
            className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50 whitespace-nowrap"
          >
            {monitorLoading || ransomLoading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {monitorLoading && (
          <div className="flex justify-center mb-8">
            <div className="w-6 h-6 border-4 border-[#9b84e2] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {keywords.length === 0 ? (
          <p className="text-gray-400 italic mb-6">
            No keywords are being monitored.
          </p>
        ) : (
          <div className="flex flex-wrap gap-3 mb-6">
            {keywords.map(({ id, keyword }) => (
              <div
                key={id}
                className="bg-[#3c3c52] px-4 py-2 rounded-full flex items-center gap-3 whitespace-normal break-words max-w-xs"
              >
                <span>{keyword}</span>
                <button
                  onClick={() => deleteKeyword(id)}
                  className="text-red-400 hover:text-red-600 font-bold"
                  aria-label={`Delete keyword ${keyword}`}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        )}

        {monitorError && (
          <p className="text-red-500 mb-4 text-center font-medium">
            {monitorError}
          </p>
        )}

        {/* Monitor Results Table */}
        <h3 className="text-2xl font-semibold mt-12 mb-2">Matched Keywords</h3>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full table-auto border border-gray-700 text-left break-words">
            <thead className="bg-[#2e2e40]">
              <tr className="text-left text-purple-300">
                <th className="py-2 px-3 border-b border-gray-700">Keyword</th>
                <th className="py-2 px-3 border-b border-gray-700">Indicator</th>
                <th className="py-2 px-3 border-b border-gray-700">Source</th>
                <th className="py-2 px-3 border-b border-gray-700">Timestamp</th>
                <th className="py-2 px-3 border-b border-gray-700">Download</th>
              </tr>
            </thead>
            <tbody>
              {monitorResults.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center text-gray-400 py-4"
                  >
                    No matches found yet.
                  </td>
                </tr>
              ) : (
                monitorResults.map((item, idx) => (
                  <tr
                    key={idx}
                    className="hover:bg-[#3c3c52] break-words"
                  >
                    <td className="px-4 py-2 border-b border-gray-700 max-w-xs">
                      {item.keyword}
                    </td>
                    <td className="px-4 py-2 border-b border-gray-700 max-w-xs">
                      {item.indicator}
                    </td>
                    <td className="px-4 py-2 border-b border-gray-700 max-w-xs">
                      {item.source}
                    </td>
                    <td className="px-4 py-2 border-b border-gray-700 whitespace-nowrap">
                      {item.matched_at
                        ? new Date(item.matched_at).toLocaleString()
                        : "—"}
                    </td>
                    <td className="px-4 py-2 border-b border-gray-700 whitespace-nowrap">
                      {item.raw_data ? (
                        <button
                          onClick={() =>
                            downloadJson(
                              item.raw_data,
                              `${item.keyword}-${item.indicator}.json`
                            )
                          }
                          className="text-blue-400 hover:text-blue-600 underline"
                        >
                          Download JSON
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls for Monitor Results */}
        {monitorTotalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={onMonitorPrevPage}
              disabled={monitorPage === 1}
              className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {monitorPage} of {monitorTotalPages}
            </span>
            <button
              onClick={onMonitorNextPage}
              disabled={monitorPage === monitorTotalPages}
              className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Ransomware Victims Section */}
        <h3 className="text-2xl font-semibold mt-12 mb-2">
          Related Third-Party Breaches
        </h3>
        <p className="text-gray-400 mb-4 text-sm">Source: Ransomware.live</p>

        {ransomLoading && (
          <div className="flex justify-center mb-8">
            <div className="w-6 h-6 border-4 border-[#9b84e2] border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {ransomError && (
          <p className="text-red-500 mb-4 text-center font-medium">{ransomError}</p>
        )}

        {!ransomLoading && ransomVictims.length === 0 && (
          <p className="text-gray-400 text-center">No related ransomware victims found.</p>
        )}

        {!ransomLoading && ransomVictims.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-gray-900 border border-gray-700 rounded-xl text-sm">
              <thead>
                <tr className="text-left text-purple-300">
                  <th className="py-2 px-3 border-b border-gray-700">Victim Name</th>
                  <th className="py-2 px-3 border-b border-gray-700">Country</th>
                  <th className="py-2 px-3 border-b border-gray-700">Sector</th>
                  <th className="py-2 px-3 border-b border-gray-700">Payment Status</th>
                  <th className="py-2 px-3 border-b border-gray-700">Attack Date</th>
                  <th className="py-2 px-3 border-b border-gray-700">Reported Date</th>
                  <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Notes</th>
                  <th className="py-2 px-3 border-b border-gray-700">Site</th>
                </tr>
              </thead>
              <tbody>
                {ransomVictims.map((v) => (
                  <tr
                    key={v.id}
                    className="text-gray-400 hover:bg-gray-800 align-top text-sm break-words"
                  >
                    <td className="py-2 px-3 border-b border-gray-700 max-w-xs">
                      {v.victim_name}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 max-w-xs">
                      {v.country || "N/A"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 max-w-xs">
                      {v.sector || "N/A"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 max-w-xs">
                      {v.payment_status || "Unknown"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 whitespace-nowrap">
                      {v.cyber_attack?.date_occurred
                        ? new Date(v.cyber_attack.date_occurred).toLocaleString()
                        : "N/A"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 whitespace-nowrap">
                      {v.cyber_attack?.date_reported
                        ? new Date(v.cyber_attack.date_reported).toLocaleString()
                        : "N/A"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700 max-w-xs overflow-hidden line-clamp-3">
                      {v.notes || "No notes"}
                    </td>
                    <td className="py-2 px-3 border-b border-gray-700">
                      {v.victim_url ? (
                        <a
                          href={
                            v.victim_url.startsWith("http")
                              ? v.victim_url
                              : `https://${v.victim_url}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-300 hover:underline"
                        >
                          Visit
                        </a>
                      ) : (
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls for Ransomware Victims */}
        {ransomTotalPages > 1 && (
          <div className="flex justify-center items-center gap-4 mt-6">
            <button
              onClick={onRansomPrevPage}
              disabled={ransomPage === 1}
              className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50"
            >
              Previous
            </button>
            <span>
              Page {ransomPage} of {ransomTotalPages}
            </span>
            <button
              onClick={onRansomNextPage}
              disabled={ransomPage === ransomTotalPages}
              className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </SearchContainer>
  );
};

export default ScoutMonitor;
